import React, { useState, useEffect, useRef } from "react";
import "./Home.css";
import { useNavigate } from "react-router-dom";
import zmLogo from "./images/LOGO_ZM.png";
import phoneIcon from "./images/phone-icon.png";
import docIcon from "./images/doc-icon.png";
import podcastIcon from "./images/podcast-icon.png";
import videoIcon from "./images/video-icon.png";
import facebookIcon from "./images/facebook-icon.png";
import instagramIcon from "./images/instagram-icon.png";
import twitterIcon from "./images/twitter-icon.png";
import whatsappIcon from "./images/whatsapp.png";
import bannerAd from "./images/banner-ad.jpg";

function getArticleUrl(article) {
  const base = window.location.origin;
  const slug = article.canonical_slug;
  const category = article.category_slug;
  if (slug && category) return `${base}/${encodeURIComponent(category)}/articulos/${encodeURIComponent(slug)}`;
  if (slug) return `${base}/news/${encodeURIComponent(slug)}`;
  return `${base}/news/${encodeURIComponent(article.id)}`;
}

function ShareButtons({ article }) {
  const url = encodeURIComponent(getArticleUrl(article));
  const title = encodeURIComponent(article.title || '');
  return (
    <div className="art-send-bar" onClick={(e) => e.stopPropagation()}>
      <span className="art-send-lbl">Compartir:</span>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
         target="_blank" rel="noopener noreferrer"
         className="art-send-item art-send-blue" aria-label="Compartir en Facebook">
        <img src={facebookIcon} alt="" aria-hidden="true" />
        <span>Facebook</span>
      </a>
      <a href={`https://twitter.com/intent/tweet?url=${url}&text=${title}`}
         target="_blank" rel="noopener noreferrer"
         className="art-send-item art-send-dark" aria-label="Compartir en Twitter">
        <img src={twitterIcon} alt="" aria-hidden="true" />
        <span>Twitter</span>
      </a>
      <a href={`https://wa.me/?text=${title}%20${url}`}
         target="_blank" rel="noopener noreferrer"
         className="art-send-item art-send-green" aria-label="Compartir en WhatsApp">
        <img src={whatsappIcon} alt="" aria-hidden="true" />
        <span>WhatsApp</span>
      </a>
    </div>
  );
}

export default function Home() {
  const navigate = useNavigate();

  // Menú móvil
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [activeMobileSection, setActiveMobileSection] = useState(null);

  // Navbar scroll (desktop)
  const navScrollRef = useRef(null);

  // Dropdown hover desktop (position:fixed — escapa overflow)
  const navItemRefs = useRef({});
  const [hoveredNavKey, setHoveredNavKey] = useState(null);
  const dropdownTimeout = useRef(null);

  // Banner
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [bannerClosing, setBannerClosing] = useState(false);

  // Datos
  const [, setAllArticles] = useState([]);
  const [latestArticle, setLatestArticle] = useState(null);
  const [recentArticles, setRecentArticles] = useState([]);
  const [mostViewedArticles, setMostViewedArticles] = useState([]);
  const [sectionArticles, setSectionArticles] = useState({});
  const [categories, setCategories] = useState([]);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper categorías (después de categories)
  const getChildCategories = React.useCallback((parentId) =>
    categories.filter(cat => !isNullLike(cat.parent_id) && String(cat.parent_id) === String(parentId)),
  [categories]);

  const API_BASE          = "https://backend-zonamicroondas.vercel.app/api";
  const RECENT_LIMIT      = 3;
  const MOST_VIEWED_LIMIT = 5;
  const SECTION_LIMIT     = 3;
  const NAV_MAX           = 6;   // categorías visibles en el navbar desktop
  const MAS_KEY           = '__mas__';
  const BANNER_LINK       = "https://festival-city-2026.boletia.com/";

  // ── utils ──
  const toTimestamp = (item) => {
    const v = item.date || item.published_at || item.created_at || item.publishedAt || item.createdAt || null;
    if (!v) return 0;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? 0 : t;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const isNullLike = (v) => {
    if (v === null || v === undefined) return true;
    if (v === 0) return true;
    if (typeof v === 'string' && v.trim() === '') return true;
    if (String(v).toLowerCase() === 'null') return true;
    return false;
  };

  // ── nav móvil ──
  const closeMobileNav = () => { setMobileNavOpen(false); setOpenSubmenu(null); setActiveMobileSection(null); };


  // ── dropdown desktop ──
  const showDrop = (key) => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
    setHoveredNavKey(key);
  };
  const hideDrop = () => {
    dropdownTimeout.current = setTimeout(() => setHoveredNavKey(null), 160);
  };
  const keepDrop = () => {
    if (dropdownTimeout.current) clearTimeout(dropdownTimeout.current);
  };
  const getDropPos = (key) => {
    const el = navItemRefs.current[key];
    if (!el) return null;
    const r = el.getBoundingClientRect();
    return { top: r.bottom, left: r.left };
  };

  // ── banner ──
  const handleDismissBanner = (e) => {
    e.preventDefault(); e.stopPropagation();
    setBannerClosing(true);
    setTimeout(() => setBannerDismissed(true), 400);
  };

  // ── navegación artículos ──
  const handleNewsNavigation = (article) => {
    if (!article) return;
    const slug = article.canonical_slug;
    const category = article.category_slug;
    if (slug && category) {
      navigate(`/${encodeURIComponent(category)}/articulos/${encodeURIComponent(slug)}`);
    } else if (slug) {
      navigate(`/news/${encodeURIComponent(slug)}`);
    } else if (article.id) {
      navigate(`/news/${encodeURIComponent(article.id)}`);
    }
    closeMobileNav();
  };

  // ── efectos ──
  useEffect(() => {
    document.body.style.overflow = mobileNavOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileNavOpen]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") closeMobileNav(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // scroll del navbar horizontal — cierra dropdown al hacer scroll en la barra
  useEffect(() => {
    const el = navScrollRef.current;
    if (!el) return;
    const onScroll = () => setHoveredNavKey(null);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // ── fetch principal ──
  useEffect(() => {
    let mounted = true;

    const fetchInitial = async () => {
      try {
        setLoading(true); setError(null);

        const mapArticle = a => ({
          id:             a.id,
          title:          a.title,
          subtitle:       a.subtitle || a.summary || "",
          date:           a.published_at || a.created_at,
          isFeatured:     [true, 1, '1', 't', 'true'].includes(a.is_featured),
          rawFeatured:    a.is_featured,
          image:          a.image_url || "/images/default-news.jpg",
          canonical_slug: a.canonical_slug || null,
          category_slug:  a.category_slug || (a.categories && a.categories.slug) || (Array.isArray(a.categories) && a.categories[0]?.slug) || '',
          category_name:  (a.categories && (a.categories.name || a.categories[0]?.name)) || a.category_name || '',
          main_category_id: a.main_category_id || null,
          views:          a.views || 0,
        });

        const byDateDesc = (a, b) => {
          const diff = toTimestamp(b) - toTimestamp(a);
          if (diff !== 0) return diff;
          return (Number(b.id) || 0) - (Number(a.id) || 0);
        };

        const nowTs = new Date();
        const isPublishable = (a) => !a.date || new Date(a.date) <= nowTs;

        // Hero: pedir la noticia destacada más reciente directamente al servidor
        const [newsRes, featuredRes] = await Promise.all([
          fetch(`${API_BASE}/news?status=published&limit=80&offset=0`),
          fetch(`${API_BASE}/news?status=published&is_featured=1&limit=1`)
        ]);
        const newsJson     = await newsRes.json();
        const featuredJson = await featuredRes.json();

        let initialArticles = [];
        if (newsJson && newsJson.success && Array.isArray(newsJson.data)) {
          initialArticles = newsJson.data.map(mapArticle).filter(isPublishable).sort(byDateDesc);
        }

        setAllArticles(initialArticles);

        let heroArticle = null;
        if (featuredJson && featuredJson.success && Array.isArray(featuredJson.data) && featuredJson.data.length > 0) {
          const mapped = mapArticle(featuredJson.data[0]);
          if (mapped.isFeatured) {
            heroArticle = mapped;
          }
        }
        if (!heroArticle) {
          const featured = initialArticles.filter(a => a.isFeatured);
          heroArticle = featured.length > 0 ? featured[0] : (initialArticles[0] || null);
        }
        setLatestArticle(heroArticle);

        // Recientes: excluyendo el héroe
        const recentList = heroArticle
          ? initialArticles.filter(a => a.id !== heroArticle.id)
          : initialArticles.slice(1);
        setRecentArticles(recentList.slice(0, RECENT_LIMIT));

        const byViews = [...initialArticles].sort((a, b) => {
          const vd = (b.views || 0) - (a.views || 0);
          if (vd !== 0) return vd;
          return byDateDesc(a, b);
        });
        setMostViewedArticles(byViews.slice(0, MOST_VIEWED_LIMIT));

        const categoriesResponse = await fetch(`${API_BASE}/categories`);
        const categoriesData     = await categoriesResponse.json();

        let catsArray = [];
        if (categoriesData && Array.isArray(categoriesData.data))           catsArray = categoriesData.data;
        else if (categoriesData && Array.isArray(categoriesData.categories)) catsArray = categoriesData.categories;
        else if (Array.isArray(categoriesData))                              catsArray = categoriesData;

        setCategories(catsArray);

        const rootSections   = catsArray.filter(cat => isNullLike(cat.parent_id));
        const getChildLocal  = (pid) => catsArray.filter(c => !isNullLike(c.parent_id) && String(c.parent_id) === String(pid));
        const baseRoots      = rootSections.length > 0 ? rootSections : catsArray;

        const sortedRoots = [...baseRoots].sort((a, b) => {
          const aH = getChildLocal(a.id).length > 0;
          const bH = getChildLocal(b.id).length > 0;
          if (aH !== bH) return aH ? -1 : 1;
          const aP = Number(a.position) || 0;
          const bP = Number(b.position) || 0;
          if (aP !== bP) return aP - bP;
          return (a.name || "").localeCompare((b.name || ""), undefined, { sensitivity: 'base' });
        });

        setSections(sortedRoots);

        const sectionArticlesObj = {};

        // Crear un mapa de category_id -> category slug para matching rápido
        const categoryIdToSlug = {};
        catsArray.forEach(cat => {
          if (cat.id) categoryIdToSlug[cat.id] = cat.slug;
        });

        for (const section of sortedRoots) {
          const sectionSlugLower = (section.slug || '').toLowerCase();
          
          // Obtener también los IDs de subcategorías de esta sección
          const childCategories = getChildCategories(section.id);
          const childIds = childCategories.map(c => c.id);
          const allCategoryIds = [section.id, ...childIds];
          
          const localMatches = initialArticles
            .filter(a => {
              // Primero verificar por main_category_id
              if (a.main_category_id && allCategoryIds.includes(a.main_category_id)) {
                return true;
              }
              // Luego por slug de categoría
              const catSlug = (a.category_slug || '').toLowerCase();
              return catSlug === sectionSlugLower;
            })
            .sort(byDateDesc);

          const picked = localMatches.slice(0, SECTION_LIMIT);
          sectionArticlesObj[section.slug] = picked;
        }

        setSectionArticles(sectionArticlesObj);
        if (!mounted) return;
        setLoading(false);
      } catch {
        if (!mounted) return;
        setError("Error al cargar las noticias.");
        setLoading(false);
      }
    };

    fetchInitial();
    return () => { mounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API_BASE]);

  // ── helpers categorías ──
  const rootCategories       = categories.filter(cat => isNullLike(cat.parent_id));
  const parentIds            = new Set(categories.map(cat => cat.parent_id));
  const referencedParents    = categories.filter(cat => parentIds.has(cat.id));
  const baseParentCategories = rootCategories.length > 0
    ? rootCategories
    : (referencedParents.length > 0 ? referencedParents : categories);

  const sortedParentCategories = [...baseParentCategories].sort((a, b) => {
    const aH = getChildCategories(a.id).length > 0;
    const bH = getChildCategories(b.id).length > 0;
    if (aH !== bH) return aH ? -1 : 1;
    const aP = Number(a.position) || 0;
    const bP = Number(b.position) || 0;
    if (aP !== bP) return aP - bP;
    return (a.name || "").localeCompare((b.name || ""), undefined, { sensitivity: 'base' });
  });

  // Desktop: primeras NAV_MAX categorías visibles + resto en "Más ▾"
  const visibleNavCats  = sortedParentCategories.slice(0, NAV_MAX);
  const overflowNavCats = sortedParentCategories.slice(NAV_MAX);

  // ── guards ──
  if (loading) return (
    <div className="home-pro">
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Cargando noticias...</p>
      </div>
    </div>
  );
  if (error) return (
    <div className="home-pro">
      <div className="error-screen"><p>{error}</p></div>
    </div>
  );

  // ────────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────────
  return (
    <div className="home-pro">

      {/* ══ HEADER + NAVBAR sticky ═══════════════════════════════════ */}
      <div className="site-top">
        <header className="header-pro">
          <div className="header-pro-inner">

            <button className="header-logo-btn" onClick={() => navigate("/")} aria-label="Ir al inicio">
              <img src={zmLogo} alt="Zona Microondas" />
            </button>

            <div className="header-brand">
              <span className="header-brand-title">Querétaro</span>
              <span className="header-brand-sub">LA NOTICIA AL MOMENTO</span>
            </div>

            <div className="header-right">
              <div className="header-socials">
                <a href="https://www.facebook.com/ZonaMicroondas" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                  <img src={facebookIcon} alt="" />
                </a>
                <a href="https://www.instagram.com/zonamicroondasqro" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                  <img src={instagramIcon} alt="" />
                </a>
                <a href="https://twitter.com" className="h-soc"
                   target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                  <img src={twitterIcon} alt="" />
                </a>
              </div>
              <div className="header-media">
                <button className="h-media" title="Documentos"><img src={docIcon} alt="Documentos" /></button>
                <button className="h-media" title="Podcast"><img src={podcastIcon} alt="Podcast" /></button>
                <button className="h-media" title="Videos"><img src={videoIcon} alt="Videos" /></button>
                <button className="h-media" title="Contacto"><img src={phoneIcon} alt="Contacto" /></button>
              </div>
            </div>

          </div>
        </header>

        {/* ══ NAVBAR categorías ════════════════════════════════════════ */}
        <nav className="navbar-pro" aria-label="Secciones principales">
          <div className="navbar-inner">

            {/* Toggle móvil */}
            <button
              className={`navbar-mob-toggle ${mobileNavOpen ? 'is-open' : ''}`}
              onClick={() => setMobileNavOpen(p => !p)}
              aria-label={mobileNavOpen ? "Cerrar menú" : "Ver secciones"}
              aria-expanded={mobileNavOpen}
            >
              <div className="toggle-bars">
                <span></span><span></span><span></span>
              </div>
              <span className="toggle-label">{mobileNavOpen ? 'CERRAR' : 'SECCIONES'}</span>
            </button>

            {/* Lista horizontal de categorías */}
            <div className={`navbar-cats ${mobileNavOpen ? 'is-open' : ''}`} ref={navScrollRef}>

              {mobileNavOpen ? (
                /* ── DRAWER MÓVIL — drill-down (sin desplazamiento de flujo) ── */
                activeMobileSection ? (
                  /* Nivel 2: subcategorías de la sección activa */
                  <>
                    <button className="nav-back-btn" onClick={() => setActiveMobileSection(null)}>
                      <svg width="8" height="12" viewBox="0 0 8 12" fill="none" aria-hidden="true">
                        <path d="M7 1L1 6L7 11" stroke="currentColor" strokeWidth="2.2"
                              strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      {activeMobileSection.name}
                    </button>
                    {getChildCategories(activeMobileSection.id).map(child => (
                      <button key={String(child.id)} className="nav-link"
                        onClick={() => { navigate(`/categoria/${child.slug}`); closeMobileNav(); }}>
                        {child.name}
                      </button>
                    ))}
                    <button className="nav-link nav-see-all-mob"
                      onClick={() => { navigate(`/categoria/${activeMobileSection.slug}`); closeMobileNav(); }}>
                      Ver todo →
                    </button>
                  </>
                ) : (
                  /* Nivel 1: lista principal */
                  <>
                    <button className="nav-link nav-home" onClick={() => { navigate("/"); closeMobileNav(); }}>
                      Inicio
                    </button>
                    {sortedParentCategories.map(parent => {
                      const children = getChildCategories(parent.id);
                      const key = String(parent.id);
                      return (
                        <div key={key} className="nav-item">
                          <button
                            className="nav-link"
                            onClick={() => {
                              if (children.length > 0) {
                                setActiveMobileSection(parent);
                              } else {
                                navigate(`/categoria/${parent.slug}`);
                                closeMobileNav();
                              }
                            }}
                          >
                            {parent.name}
                            {children.length > 0 && (
                              <svg className="nav-chevron" width="10" height="6"
                                   viewBox="0 0 10 6" fill="none" aria-hidden="true">
                                <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </>
                )
              ) : (
                /* ── NAVBAR DESKTOP — sin cambios ── */
                <>
                  <button className="nav-link nav-home" onClick={() => navigate("/")}>
                    Inicio
                  </button>

                  {visibleNavCats.map(parent => {
                    const children  = getChildCategories(parent.id);
                    const key       = String(parent.id);
                    const isHovered = hoveredNavKey === key;
                    return (
                      <div
                        key={key}
                        className={`nav-item ${isHovered ? 'is-hovered' : ''}`}
                        ref={el => { navItemRefs.current[key] = el; }}
                        onMouseEnter={() => { if (children.length > 0) showDrop(key); }}
                        onMouseLeave={hideDrop}
                      >
                        <button
                          className="nav-link"
                          aria-haspopup={children.length > 0 || undefined}
                          aria-expanded={isHovered || undefined}
                          onClick={() => { navigate(`/categoria/${parent.slug}`); }}
                        >
                          {parent.name}
                          {children.length > 0 && (
                            <svg className={`nav-chevron ${isHovered ? 'rotated' : ''}`}
                                 width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                              <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                    strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          )}
                        </button>
                        {children.length > 0 && isHovered && (() => {
                          const pos = getDropPos(key);
                          if (!pos) return null;
                          return (
                            <div className="nav-subdrop-fixed"
                              style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
                              onMouseEnter={keepDrop} onMouseLeave={hideDrop}>
                              {children.map(child => (
                                <button key={String(child.id)} className="nav-subdrop-item"
                                  onClick={() => { navigate(`/categoria/${child.slug}`); }}>
                                  {child.name}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}

                  {overflowNavCats.length > 0 && (() => {
                    const isHovered = hoveredNavKey === MAS_KEY;
                    const subOpen   = openSubmenu === MAS_KEY;
                    return (
                      <div
                        className={`nav-item ${isHovered ? 'is-hovered' : ''}`}
                        ref={el => { navItemRefs.current[MAS_KEY] = el; }}
                        onMouseEnter={() => showDrop(MAS_KEY)}
                        onMouseLeave={hideDrop}
                      >
                        <button className="nav-link nav-mas" aria-haspopup="true"
                          aria-expanded={isHovered || undefined}>
                          Más
                          <svg className={`nav-chevron ${isHovered || subOpen ? 'rotated' : ''}`}
                               width="10" height="6" viewBox="0 0 10 6" fill="none" aria-hidden="true">
                            <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="2"
                                  strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                        {isHovered && (() => {
                          const pos = getDropPos(MAS_KEY);
                          if (!pos) return null;
                          return (
                            <div className="nav-subdrop-fixed"
                              style={{ top: `${pos.top}px`, left: `${pos.left}px` }}
                              onMouseEnter={keepDrop} onMouseLeave={hideDrop}>
                              {overflowNavCats.map(cat => (
                                <button key={String(cat.id)} className="nav-subdrop-item"
                                  onClick={() => { navigate(`/categoria/${cat.slug}`); }}>
                                  {cat.name}
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })()}
                </>
              )}
            </div>

            {/* Handle */}
            <span className="navbar-handle">@ZONAMICROONDAS</span>
          </div>

          {mobileNavOpen && (
            <div className="navbar-overlay" onClick={closeMobileNav} aria-hidden="true"></div>
          )}
        </nav>
      </div>{/* /site-top */}

      {/* ══ HERO ════════════════════════════════════════════════════ */}
      {latestArticle && (
        <section
          className="hero-home"
          style={{ backgroundImage: `url(${latestArticle.image})` }}
          onClick={() => handleNewsNavigation(latestArticle)}
          aria-label={`Noticia principal: ${latestArticle.title}`}
        >
          <div className="hero-home-overlay">
            <div className="hero-home-content">
              <span className="hero-home-badge">
                <span className="hero-dot" aria-hidden="true">●</span>
                {formatDate(latestArticle.date)}
              </span>
              <h1 className="hero-home-title">{latestArticle.title}</h1>
              {latestArticle.subtitle && (
                <p className="hero-home-sub">{latestArticle.subtitle}</p>
              )}
              <button
                className="hero-home-cta"
                onClick={(e) => { e.stopPropagation(); handleNewsNavigation(latestArticle); }}
              >
                Leer artículo completo
                <span className="hero-arrow" aria-hidden="true">→</span>
              </button>
            </div>
          </div>
        </section>
      )}

      {/* ══ CUERPO ══════════════════════════════════════════════════ */}
      <div className="home-body">
        <div className="home-body-inner">

          {/* ── COLUMNA PRINCIPAL ────────────────────────────────── */}
          <div className="home-main-col">

            {/* Últimas noticias */}
            {recentArticles.length > 0 && (
              <section className="home-block">
                <div className="hb-header">
                  <div className="hb-accent"></div>
                  <h2 className="hb-title">Últimas Noticias</h2>
                </div>
                <div className="recents-grid">
                  {recentArticles.map(article => (
                    <article key={article.id} className="recent-card"
                      onClick={() => handleNewsNavigation(article)}>
                      <div className="rc-img-wrap">
                        <img src={article.image} alt={article.title} loading="lazy" />
                      </div>
                      <div className="rc-body">
                        <time className="rc-date">{formatDate(article.date)}</time>
                        <h3 className="rc-title">{article.title}</h3>
                        {article.subtitle && <p className="rc-excerpt">{article.subtitle}</p>}
                        <button className="rc-cta"
                          onClick={(e) => { e.stopPropagation(); handleNewsNavigation(article); }}>
                          Leer más <span aria-hidden="true">→</span>
                        </button>
                        <ShareButtons article={article} />
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* Secciones con subcategorías */}
            {sections
              .filter(section => (sectionArticles[section.slug] || []).length > 0)
              .map(section => {
                const articles  = sectionArticles[section.slug] || [];
                const subCats   = getChildCategories(section.id);
                return (
                  <section key={section.id} className="home-block">
                    <div className="hb-header">
                      <div className="hb-accent"></div>
                      <h2 className="hb-title">{section.name}</h2>
                      <button className="hb-see-all"
                        onClick={() => navigate(`/categoria/${section.slug}`)}>
                        Ver todo →
                      </button>
                    </div>

                    {/* Chips de subcategorías */}
                    {subCats.length > 0 && (
                      <div className="section-subcats">
                        {subCats.map(sub => (
                          <button key={String(sub.id)} className="subcat-pill"
                            onClick={() => navigate(`/categoria/${sub.slug}`)}>
                            {sub.name}
                          </button>
                        ))}
                      </div>
                    )}

                    <div className={`section-grid ${articles.length >= 3 ? 'sg-has-featured' : ''}`}>
                      {articles.map((article, i) => (
                        <article
                          key={article.id}
                          className={`sc ${i === 0 && articles.length >= 3 ? 'sc-featured' : ''}`}
                          onClick={() => handleNewsNavigation(article)}
                        >
                          <div className="sc-img-wrap">
                            <img src={article.image} alt={article.title} loading="lazy" />
                          </div>
                          <div className="sc-body">
                            <time className="sc-date">{formatDate(article.date)}</time>
                            <h3 className="sc-title">{article.title}</h3>
                            <ShareButtons article={article} />
                          </div>
                        </article>
                      ))}
                    </div>
                  </section>
                );
              })}
          </div>

          {/* ── SIDEBAR ──────────────────────────────────────────── */}
          <aside className="home-sidebar" aria-label="Panel lateral">

            {/* Más Vistas */}
            <section className="sb-widget">
              <div className="hb-header">
                <div className="hb-accent"></div>
                <h3 className="hb-title">Más Vistas</h3>
              </div>
              <div className="sb-list">
                {mostViewedArticles.map((article, i) => (
                  <article key={article.id} className="sb-item"
                    onClick={() => handleNewsNavigation(article)}>
                    <span className="sb-rank" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
                    <div className="sb-img">
                      <img src={article.image} alt={article.title} loading="lazy" />
                    </div>
                    <div className="sb-info">
                      <h4 className="sb-item-title">{article.title}</h4>
                      <time className="sb-item-date">{formatDate(article.date)}</time>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            {/* Contacto */}
            <section className="sb-widget sb-contact">
              <div className="hb-header">
                <div className="hb-accent"></div>
                <h3 className="hb-title">Contacto</h3>
              </div>
              <div className="contact-list">
                <a href="tel:4423533992" className="contact-row">
                  <span className="contact-ico" aria-hidden="true">📞</span>
                  <span>442 353 3992</span>
                </a>
                <a href="mailto:zonamicroondas@gmail.com" className="contact-row">
                  <span className="contact-ico" aria-hidden="true">✉️</span>
                  <span>zonamicroondas@gmail.com</span>
                </a>
              </div>
            </section>

            {/* Banner dismissible */}
            {!bannerDismissed && (
              <div className={`sb-banner ${bannerClosing ? 'is-closing' : ''}`} aria-live="polite">
                <div className="banner-topbar">
                  <span className="banner-pub-label">Publicidad</span>
                  <button className="banner-close" onClick={handleDismissBanner}
                    aria-label="Cerrar publicidad">
                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
                      <path d="M1 1L10 10M10 1L1 10" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
                <a href={BANNER_LINK} target="_blank" rel="noopener noreferrer"
                   className="banner-img-link">
                  <img src={bannerAd} alt="Publicidad" className="banner-ad-img" />
                </a>
              </div>
            )}

            {/* Síguenos */}
            <section className="sb-widget sb-social">
              <div className="hb-header">
                <div className="hb-accent"></div>
                <h3 className="hb-title">Síguenos</h3>
              </div>
              <div className="social-widget-list">
                <a href="https://www.facebook.com/ZonaMicroondas"
                   className="sw-item sw-fb" target="_blank" rel="noopener noreferrer">
                  <img src={facebookIcon} alt="" aria-hidden="true" />
                  <div>
                    <span className="sw-name">Facebook</span>
                    <span className="sw-handle">@ZonaMicroondas</span>
                  </div>
                </a>
                <a href="https://www.instagram.com/zonamicroondasqro"
                   className="sw-item sw-ig" target="_blank" rel="noopener noreferrer">
                  <img src={instagramIcon} alt="" aria-hidden="true" />
                  <div>
                    <span className="sw-name">Instagram</span>
                    <span className="sw-handle">@zonamicroondasqro</span>
                  </div>
                </a>
                <a href="https://twitter.com"
                   className="sw-item sw-tw" target="_blank" rel="noopener noreferrer">
                  <img src={twitterIcon} alt="" aria-hidden="true" />
                  <div>
                    <span className="sw-name">Twitter</span>
                    <span className="sw-handle">@ZonaMicroondas</span>
                  </div>
                </a>
              </div>
            </section>

          </aside>
        </div>
      </div>

      {/* ══ FOOTER ══════════════════════════════════════════════════ */}
      <footer className="footer-pro">
        <p className="footer-pro-text">
          Copyright © Todos los derechos reservados | ZONAMICROONDAS. De no existir previa
          autorización, queda expresamente prohibida la publicación, retransmisión, edición
          y cualquier otro uso de los contenidos.
        </p>
      </footer>

    </div>
  );
}
